<template>
  <div class="login-page">
    <!-- 氛围背景装饰 -->
    <div class="bg-blob blob-1"></div>
    <div class="bg-blob blob-2"></div>
    <div class="bg-grain"></div>

    <div class="login-card">
      <div class="brand">
        <div class="brand-badge"><span>✦</span></div>
        <h1 class="brand-title">星果</h1>
        <p class="brand-sub">互动小说创作后台</p>
      </div>

      <el-form :model="form" label-position="top" class="login-form" @submit.native.prevent>
        <el-form-item label="用户名">
          <el-input v-model="form.username" placeholder="请输入用户名" />
        </el-form-item>
        <el-form-item label="密码">
          <el-input
            v-model="form.password"
            type="password"
            show-password
            placeholder="请输入密码"
            @keyup.enter.native="login"
          />
        </el-form-item>
        <el-button type="primary" class="submit-btn" :loading="loading" @click="login">登 录</el-button>
      </el-form>

      <div v-if="socialLoginEnabled" class="divider"><span>其他方式登录</span></div>

      <div v-if="socialLoginEnabled" class="social-row">
        <el-button class="social-btn wx-btn" @click="wxLogin">
          <i class="el-icon-chat-dot-round"></i><span>微信扫码</span>
        </el-button>
        <el-button class="social-btn qq-btn" @click="qqLogin">
          <i class="el-icon-user"></i><span>QQ 扫码</span>
        </el-button>
      </div>

      <p class="footer-tip">登录即代表同意遵守平台创作规范</p>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Component } from 'vue-property-decorator'
import { login as loginApi, getWxQrConnectUrl, getQqAuthorizeUrl } from '@/api/auth'
import { setToken } from '@/utils/auth'
import store from '@/store'

// 扫码登录的 state 存储 key，回调页用它校验防 CSRF
const WX_LOGIN_STATE_KEY = 'WX_LOGIN_STATE'
// QQ 扫码登录的 state 存储 key，回调页用它校验防 CSRF
const QQ_LOGIN_STATE_KEY = 'QQ_LOGIN_STATE'

@Component
export default class Login extends Vue {
  socialLoginEnabled = process.env.VUE_APP_ENABLE_SOCIAL_LOGIN !== 'false'

  form = {
    username: '',
    password: ''
  }

  loading = false

  async login() {
    this.loading = true
    try {
      const res = await loginApi(this.form)
      let r = res.data
      setToken(r.token)
      store.commit('user/SET_USER', r.user)
      // 登录后直接进入「我的作品」
      await this.$router.push('/my-works')
    } catch (err) {
      this.$message.error('登录失败')
    } finally {
      this.loading = false
    }
  }

  wxLogin() {
    if (!process.env.VUE_APP_WX_WEBAPP_ID) {
      this.$message.error('微信扫码登录未配置（缺少网站应用 AppID）')
      return
    }
    // 生成随机 state 防 CSRF，存入 sessionStorage，回调页比对
    const state = Math.random().toString(36).slice(2) + Date.now().toString(36)
    sessionStorage.setItem(WX_LOGIN_STATE_KEY, state)
    window.location.href = getWxQrConnectUrl(state)
  }

  qqLogin() {
    if (!process.env.VUE_APP_QQ_WEBAPP_ID) {
      this.$message.error('QQ 扫码登录未配置（缺少网站应用 AppID）')
      return
    }
    // 生成随机 state 防 CSRF，存入 sessionStorage，回调页比对
    const state = Math.random().toString(36).slice(2) + Date.now().toString(36)
    sessionStorage.setItem(QQ_LOGIN_STATE_KEY, state)
    window.location.href = getQqAuthorizeUrl(state)
  }
}
</script>

<style scoped>
.login-page {
  --brand-1: #7b7cf0;
  --brand-2: #c187f2;
  --accent-rose: #ff9ec7;
  --ink: #2f2d40;
  --muted: #9a98ad;
  --wx: #07c160;
  --qq: #12b7f5;

  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow: hidden;
  background:
    radial-gradient(1200px 600px at 12% -10%, #ece9ff 0%, transparent 55%),
    radial-gradient(1000px 700px at 110% 20%, #ffe6f1 0%, transparent 50%),
    linear-gradient(165deg, #f4f2ff 0%, #fbeef6 52%, #fff6ee 100%);
}

/* 背景柔光球 */
.bg-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(70px);
  opacity: 0.55;
  pointer-events: none;
  will-change: transform;
}
.blob-1 {
  width: 460px;
  height: 460px;
  top: -120px;
  left: -100px;
  background: radial-gradient(circle at 30% 30%, #b6b3ff, #8f8cf2 70%);
  animation: floatBlob 18s ease-in-out infinite;
}
.blob-2 {
  width: 420px;
  height: 420px;
  bottom: -140px;
  right: -110px;
  background: radial-gradient(circle at 60% 40%, #ffc2dd, #ff9ec7 70%);
  animation: floatBlob 22s ease-in-out infinite reverse;
}

/* 颗粒质感叠加 */
.bg-grain {
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.5;
  mix-blend-mode: soft-light;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
}

/* 登录卡片 */
.login-card {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 392px;
  padding: 42px 38px 30px;
  border-radius: 26px;
  background: rgba(255, 255, 255, 0.74);
  backdrop-filter: blur(22px) saturate(150%);
  -webkit-backdrop-filter: blur(22px) saturate(150%);
  border: 1px solid rgba(255, 255, 255, 0.7);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.8) inset,
    0 30px 60px -22px rgba(96, 84, 180, 0.34),
    0 12px 28px -18px rgba(150, 90, 140, 0.22);
  animation: riseIn 0.7s cubic-bezier(0.2, 0.75, 0.2, 1) both;
}

/* 品牌区 */
.brand {
  text-align: center;
  margin-bottom: 26px;
  animation: riseIn 0.7s cubic-bezier(0.2, 0.75, 0.2, 1) 0.06s both;
}
.brand-badge {
  width: 60px;
  height: 60px;
  margin: 0 auto 14px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 30px;
  color: #fff;
  background: linear-gradient(140deg, var(--brand-1), var(--brand-2) 60%, var(--accent-rose));
  box-shadow:
    0 10px 22px -6px rgba(123, 124, 240, 0.55),
    0 2px 0 rgba(255, 255, 255, 0.5) inset;
  transform: rotate(-4deg);
}
.brand-badge span {
  transform: rotate(4deg);
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}
.brand-title {
  margin: 0;
  font-family: "Songti SC", "STSong", "SimSun", serif;
  font-size: 30px;
  font-weight: 700;
  letter-spacing: 4px;
  color: var(--ink);
}
.brand-sub {
  margin: 6px 0 0;
  font-size: 13px;
  letter-spacing: 1.5px;
  color: var(--muted);
}

.login-form {
  animation: riseIn 0.7s cubic-bezier(0.2, 0.75, 0.2, 1) 0.12s both;
}

/* Element 表单深度定制 */
.login-form ::v-deep .el-form-item {
  margin-bottom: 18px;
}
.login-form ::v-deep .el-form-item__label {
  padding: 0 0 6px 2px;
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 1px;
  line-height: 1.2;
  color: #8b8aa1;
}
.login-form ::v-deep .el-input__inner {
  height: 48px;
  line-height: 48px;
  padding: 0 16px;
  border-radius: 14px;
  border: 1.5px solid rgba(123, 124, 240, 0.16);
  background: rgba(250, 250, 255, 0.85);
  color: var(--ink);
  font-size: 15px;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
}
.login-form ::v-deep .el-input__inner::placeholder {
  color: #b8b6c8;
}
.login-form ::v-deep .el-input__inner:hover {
  border-color: rgba(123, 124, 240, 0.35);
}
.login-form ::v-deep .el-input__inner:focus {
  border-color: var(--brand-1);
  background: #fff;
  box-shadow: 0 0 0 4px rgba(123, 124, 240, 0.14);
}
.login-form ::v-deep .el-input__suffix {
  right: 8px;
}

/* 主登录按钮 */
.submit-btn {
  width: 100%;
  height: 50px;
  margin-top: 6px;
  border: none !important;
  border-radius: 14px;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 6px;
  color: #fff !important;
  background: linear-gradient(135deg, var(--brand-1), var(--brand-2) 55%, #d58cef) !important;
  box-shadow: 0 14px 26px -10px rgba(123, 124, 240, 0.6);
  transition: transform 0.18s, box-shadow 0.18s, filter 0.18s;
}
.submit-btn:hover {
  transform: translateY(-2px);
  filter: brightness(1.04);
  box-shadow: 0 18px 32px -10px rgba(123, 124, 240, 0.7);
}
.submit-btn:active {
  transform: translateY(0);
}

/* 分隔线 */
.divider {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 22px 0 16px;
  animation: riseIn 0.7s cubic-bezier(0.2, 0.75, 0.2, 1) 0.18s both;
}
.divider::before,
.divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(120, 110, 160, 0.22), transparent);
}
.divider span {
  font-size: 12px;
  letter-spacing: 1px;
  color: #aaa8ba;
  white-space: nowrap;
}

/* 第三方登录 */
.social-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  animation: riseIn 0.7s cubic-bezier(0.2, 0.75, 0.2, 1) 0.24s both;
}
.social-btn {
  width: 100%;
  height: 46px;
  margin: 0 !important;
  border-radius: 14px;
  border: 1.5px solid #ebe9f2 !important;
  background: rgba(255, 255, 255, 0.7) !important;
  color: #6b6980 !important;
  font-size: 14px;
  font-weight: 500;
  transition: transform 0.16s, border-color 0.16s, box-shadow 0.16s, background 0.16s;
}
.social-btn i {
  margin-right: 6px;
  font-size: 17px;
  vertical-align: -1px;
}
.social-btn:hover {
  transform: translateY(-2px);
  background: #fff !important;
}
.wx-btn i {
  color: var(--wx);
}
.wx-btn:hover {
  border-color: rgba(7, 193, 96, 0.55) !important;
  box-shadow: 0 10px 20px -10px rgba(7, 193, 96, 0.5);
}
.qq-btn i {
  color: var(--qq);
}
.qq-btn:hover {
  border-color: rgba(18, 183, 245, 0.55) !important;
  box-shadow: 0 10px 20px -10px rgba(18, 183, 245, 0.5);
}

.footer-tip {
  margin: 22px 0 4px;
  text-align: center;
  font-size: 11.5px;
  letter-spacing: 0.5px;
  color: #b4b2c2;
  animation: riseIn 0.7s cubic-bezier(0.2, 0.75, 0.2, 1) 0.3s both;
}

@keyframes riseIn {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes floatBlob {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  50% {
    transform: translate(26px, -34px) scale(1.08);
  }
}

@media (max-width: 480px) {
  .login-card {
    padding: 34px 24px 26px;
    border-radius: 22px;
  }
  .brand-title {
    font-size: 27px;
  }
}

/* 尊重减少动画偏好 */
@media (prefers-reduced-motion: reduce) {
  .bg-blob,
  .login-card,
  .brand,
  .login-form,
  .divider,
  .social-row,
  .footer-tip {
    animation: none !important;
  }
}
</style>
